import { DictType } from "@pma/types";
import { useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { cn } from "@pma/utils/helpers";
import { Button } from "@components/common/button";
import axios from "axios";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useAuth } from "@stores/auth-store";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  EDIT_USER_ORG_PARAMETER_ENDPOINT,
  SERVER_URL,
  USER_ROLE_DICTIONARY_ENDPOINT,
} from "@pma/utils/api-routes";
import useTranslator from "@hooks/use-translator";
import { Separator } from "@components/common/separator";
import { toast } from "@hooks/use-toast";

interface Props {
  role?: string;
  refetch: CallableFunction;
  userId: string;
}
const RolesSelector = ({ role, userId, refetch }: Props) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(role);

  const { authConfig } = useAuth();

  const { translate, locale } = useTranslator();

  const isHebrew = locale === "he";

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const orgId = authConfig?.last_organization_id;

  const getRoles = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${USER_ROLE_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data } = useQuery({
    queryKey: ["roles", orgId],
    queryFn: getRoles,
  });

  const updateUserDetails = async (dep: DictType) => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,

      {
        user_id: userId,
        role_id: dep.id,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const updateUserRoleMutation = useMutation({
    mutationKey: [`roles`],
    mutationFn: updateUserDetails,
    onSuccess: (data) => {
      setOpen(false);

      refetch && refetch();
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data.message ||
                translate(`Error occurred while updating`) +
                  " " +
                  translate(`role`),
        variant: "error",
      });
    },
  });

  useEffect(() => {
    role && setValue(role);
  }, [role]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="w-full h-8 text-left flex items-center justify-start p-1 text-muted-foreground"
        >
          {role ? role : translate("Select Role")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <CommandInput placeholder={translate("Search for role") + "..."} />
          <CommandList className={cn(isHebrew ? "rtl" : "ltr")}>
            <CommandEmpty>{translate("No roles found")}.</CommandEmpty>
            <CommandGroup>
              {data?.map((role: DictType) => (
                <CommandItem
                  key={role.id}
                  value={role?.name}
                  onSelect={(currentValue: any) => {
                    setValue(role?.name);
                    updateUserRoleMutation.mutate(role);
                  }}
                  className={cn(
                    "flex flex-col text-left items-start my-1 text-black",
                  )}
                >
                  {role.name}
                  <Separator className="mt-1" />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default RolesSelector;
